import { useSuspenseQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo } from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Card,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    LoadingIndicator,
} from '@gitbook/spine-react';
import { useLiveRef } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { getOpenAPIProcessingErrorMessage } from './OpenAPIProcessingError';
import './react-openapi.css';

import { OpenAPISpecEditDialog } from './OpenAPISpecEditDialog';

/**
 * Wait for specification to be processed and ready to be used.
 */
export function OpenAPIWaitForSpec(props: {
    organizationId: string;
    specSlug: string;
    errorAction?: ReactNode;
    onReady?: () => void;
    onError?: () => void;
}) {
    const { organizationId, specSlug } = props;
    const { data: openAPISpec } = useSuspenseQuery(
        apiQuery('getOpenAPISpecBySlug', {
            organizationId,
            specSlug,
        })
    );
    const propsRef = useLiveRef(props);

    const state = useMemo(():
        | { status: 'processing' }
        | { status: 'error'; errorCode: api.OpenAPISpecProcessingErrorCode }
        | { status: 'ready' } => {
        if (openAPISpec.processingState === api.OpenAPISpecProcessingState.Complete) {
            if (openAPISpec.lastProcessErrorCode) {
                return { status: 'error', errorCode: openAPISpec.lastProcessErrorCode };
            }
            return { status: 'ready' };
        }

        return { status: 'processing' };
    }, [openAPISpec]);

    useEffect(() => {
        if (state.status === 'ready') {
            propsRef.current.onReady?.();
        } else if (state.status === 'error') {
            propsRef.current.onError?.();
        }
    }, [state.status, propsRef]);

    switch (state.status) {
        case 'processing': {
            return (
                <EmptyState>
                    <EmptyStateIcon>
                        <LoadingIndicator />
                    </EmptyStateIcon>
                    <Heading>Processing OpenAPI specification</Heading>
                    <Description>
                        Your OpenAPI specification is currently being processed.
                        <br />
                        This may take up to 2 minutes.
                    </Description>
                </EmptyState>
            );
        }
        case 'error': {
            const {
                errorAction = (
                    <DialogRoot>
                        <DialogTrigger>
                            <Button label="Edit specification" />
                        </DialogTrigger>
                        <OpenAPISpecEditDialog
                            openAPISpec={openAPISpec}
                            organizationId={organizationId}
                        />
                    </DialogRoot>
                ),
            } = props;

            return (
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.Alert />
                    </EmptyStateIcon>
                    <Heading>Failed to process OpenAPI specification</Heading>
                    <Description>{getOpenAPIProcessingErrorMessage(state.errorCode)}</Description>
                    {openAPISpec.lastProcessedErrors &&
                    openAPISpec.lastProcessedErrors.length > 0 ? (
                        <Errors errors={openAPISpec.lastProcessedErrors} />
                    ) : null}
                    <EmptyStateActions>{errorAction}</EmptyStateActions>
                </EmptyState>
            );
        }
        case 'ready': {
            return null;
        }
        default: {
            assertNever(state);
        }
    }
}

function Errors(props: { errors: api.OpenAPIErrorObject[] }) {
    const { errors } = props;
    const singleError = errors.length === 1 ? errors[0] : null;
    return (
        <Card kind="glass" className="mt-2 p-4 text-left">
            {singleError ? (
                <>
                    <div className="mb-2 font-bold">Detail of error</div>
                    <div>{singleError.message}</div>
                </>
            ) : (
                <>
                    <div className="mb-2 font-bold">Errors</div>
                    <ul className="list-inside list-disc">
                        {errors.map((error, index) => (
                            <li key={index}>{error.message}</li>
                        ))}
                    </ul>
                </>
            )}
        </Card>
    );
}
